import React, { useState, useEffect, useRef } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import { Calendar } from "components/ui/calendar";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import {
  Estimate,
  FunnelNames,
  LectureType,
  staticLectureClass,
} from "lib/types";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";
import { toast } from "sonner";
import ResultPage from "./_Result";
import { apiPost, apiPut } from "components/Common/Axios";
import SeparatorWithText from "components/Common/separator-with-text";
import { Link } from "react-router-dom";

const EstimatePage = () => {
  const [step, setStep] = useState<number>(1);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [classTimes, setClassTimes] = useState<number[]>([2, 3, 4]);
  const [isEnableNext, setIsEnableNext] = useState<boolean>(false);
  const [isEnableSubmit, setIsEnableSubmit] = useState<boolean>(false);
  const [isDateSelected, setIsDateSelected] = useState<boolean>(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  const [estimate, setEstimate] = useState<Estimate>({
    id: 0,
    classType: 1,
    schoolName: "",
    classTimes: 3,
    contractCnt: 0,
    classDate: new Date(),
    teacherName: "",
    teacherEmail: "",
    teacherPhone: "",
    funnels: "",
    agreeTerms: false,
    agreePrivacy: false,
    agreeAd: false,
    price: 0,
    isIncludeLunch: false,
    launchMinutes: 0,
    requestClassTimeFrom: "",
    requestClassTimeTo: "",
    adminStaffEmail: "",
    adminStaffPhone: "",
    adminStaffName: "",
    isRequestEstimate: false,
    requestEstimateDateTime: new Date(),
    isCreateDocument: false,
    isSendEmail: false,
    isSendSms: false,
    isConfirmSchool: false,
    isNeedSendConfirmSms: false,
    timeToSendConfirmSms: new Date(),
    isAgreeContract: false,
    isApplyCompleted: false,
    isContractComplete: false,
    schoolSalesId: 0,
    isFree: false,
  });

  // estimate나 isDateSelected가 변경될 때마다 버튼 상태 업데이트
  useEffect(() => {
    const isValid = checkValid1(estimate);
    console.log("isValid", isValid);
    setIsEnableNext(isValid);
  }, [estimate, isDateSelected]);

  // 신청자 이름 입력 필드에 대한 ref
  const teacherNameInputRef = useRef<HTMLInputElement>(null);

  const createNewEstimate = () => {
    setIsCompleted(false);
    setStep(1);
    setEstimate({
      id: 0,
      classType: 1,
      schoolName: "",
      classTimes: 3,
      contractCnt: 0,
      classDate: new Date(),
      teacherName: "",
      teacherEmail: "",
      teacherPhone: "",
      funnels: "",
      agreeTerms: false,
      agreePrivacy: false,
      agreeAd: false,
      price: 0,
      isIncludeLunch: false,
      launchMinutes: 0,
      requestClassTimeFrom: "",
      requestClassTimeTo: "",
      adminStaffEmail: "",
      adminStaffPhone: "",
      adminStaffName: "",
      isRequestEstimate: false,
      requestEstimateDateTime: new Date(),
      isCreateDocument: false,
      isSendEmail: false,
      isSendSms: false,
      isConfirmSchool: false,
      isNeedSendConfirmSms: false,
      timeToSendConfirmSms: new Date(),
      isAgreeContract: false,
      isApplyCompleted: false,
      isContractComplete: false,
      schoolSalesId: 0,
      isFree: false,
    });
  };

  const handleInputChange = (field: keyof Estimate, value: any) => {
    if (field === "classType") {
      let newClassTimes = [];
      if (value === LectureType.startup) {
        newClassTimes = [2, 3, 4];
      } else if (value === LectureType.startupCompetition) {
        newClassTimes = [4, 5, 6];
      } else if (value === LectureType.ai) {
        newClassTimes = [2, 3];
      } else if (value === LectureType.makejob) {
        newClassTimes = [2, 3, 4];
      } else if (value === LectureType.economy) {
        newClassTimes = [2, 3, 4];
      } else {
        newClassTimes = [2, 3, 4];
      }

      setClassTimes(newClassTimes);
      if (!newClassTimes.includes(estimate.classTimes)) {
        setEstimate({
          ...estimate,
          classType: value,
          classTimes: newClassTimes[newClassTimes.length - 1],
        });
      } else {
        setEstimate({
          ...estimate,
          classType: value,
        });
      }
      return;
    }

    if (field === "classDate") {
      const isFutureDate =
        value && format(value, "yyyy-MM-dd") > format(new Date(), "yyyy-MM-dd");
      setIsDateSelected(isFutureDate);
    }

    const newEstimate = {
      ...estimate,
      [field]: value,
    };

    setEstimate(newEstimate);
    setIsEnableSubmit(checkValid2(newEstimate));
  };

  const checkValid1 = (newEstimate: Estimate) => {
    return (
      classTimes.includes(newEstimate.classTimes) &&
      newEstimate.classType !== 0 &&
      newEstimate.schoolName !== "" &&
      newEstimate.contractCnt !== 0 &&
      newEstimate.classTimes !== 0 &&
      newEstimate.classDate &&
      newEstimate.classDate instanceof Date &&
      !isNaN(newEstimate.classDate.getTime()) &&
      format(newEstimate.classDate, "yyyy-MM-dd") >
        format(new Date(), "yyyy-MM-dd") &&
      isDateSelected
    );
  };

  const checkValid2 = (newEstimate: Estimate) => {
    return (
      newEstimate.teacherName !== "" &&
      newEstimate.teacherEmail !== "" &&
      newEstimate.teacherPhone !== "" &&
      newEstimate.agreeTerms &&
      newEstimate.agreePrivacy
    );
  };

  const handleNext = () => {
    if (step === 1) {
      // 첫 번째 단계 유효성 검사
      if (!checkValid1(estimate)) {
        toast.error("모든 필수 항목을 입력해주세요.");
        return;
      }
      setStep(2);
      // 다음 단계로 이동 후 상단으로 스크롤하고 신청자 이름 입력 필드에 포커스
      setTimeout(() => {
        window.scrollTo(0, 0);
        teacherNameInputRef.current?.focus();
      }, 100);
    }
  };

  const handlePrev = () => {
    setStep(1);
  };

  const handleSubmit = async () => {
    // 두 번째 단계 유효성 검사
    if (!estimate.agreeTerms || !estimate.agreePrivacy) {
      toast.error("필수 동의 사항을 모두 체크해주세요.");
      return;
    }

    let response;
    if (estimate.id === 0) {
      // 신규 견적 요청
      setIsEditMode(false);
      response = await apiPost("/estimate", estimate);
      if (response.error || !response.data) {
        console.log("견적 요청 실패:", response.error, response.data);
        toast.error("견적 요청에 실패했습니다.");
        return;
      }

      const estimateId = response.data;
      if (estimateId) {
        setEstimate({
          ...estimate,
          id: estimateId,
        });
      }
    } else {
      // 견적 수정
      setIsEditMode(true);
      response = await apiPut(`/estimate`, estimate);
      if (response.error || !response.data) {
        console.log("견적 수정 실패:", response.error, response.data);
        toast.error("견적 수정에 실패했습니다.");
        return;
      }
    }

    console.log("견적 요청 데이터:", estimate);
    setIsCompleted(true);
    // 완료 페이지로 스크롤
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="px-16 py-8 bg-gray-100 rounded-3xl border border-gray-500 flex flex-col md:flex-row items-center justify-between mb-10 gap-4">
          <div className="text-xl font-semibold">바로 수업신청하기</div>
          <div className="flex items-center">
            <Link
              to="https://pf.kakao.com/_kDamG/chat?api_ver=1.1&kakao_agent=sdk%2F1.43.6%20os%2Fjavascript%20sdk_type%2Fjavascript%20lang%2Fko-KR%20device%2FWin32%20origin%2Fhttps%253A%252F%252Fwww.workwise.guru&app_key=dd23c3250403170e288e853acc27cec1&referer=https%3A%2F%2Fwww.workwise.guru%2F33"
              target="_blank"
            >
              <Button
                variant="primary"
                className="ml-4 bg-yellow-200 text-yellow-800 border-yellow-900 hover:bg-yellow-300"
              >
                <img
                  src="/images/kakao.svg"
                  alt="kakao"
                  className="w-4 h-4 mr-2"
                />
                카카오톡 신청
              </Button>
            </Link>
            <Link to="tel:0317023336" target="_blank">
              <Button
                variant="primary"
                className="ml-4 bg-blue-200 border-gray-200 hover:bg-blue-300 text-blue-900"
              >
                <img
                  src="/images/phone.svg"
                  alt="phone"
                  className="w-4 h-4 mr-2"
                />{" "}
                전화신청
              </Button>
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex gap-2 items-end mb-2">
            <div className="text-3xl font-semibold">플러스멘토 수업</div>
            <div className="text-2xl font-semibold text-gray-600">견적문의</div>
          </div>
          <div className="text-gray-400 text-xl mb-10">
            프로그램을 선택해주시면 메일로 수업의 기획안과 견적서를 보내드려요{" "}
          </div>

          {step === 1 && (
            <>
              <div className="space-y-3 mb-8">
                <div className="h-3 bg-indigo-500 w-full"></div>
                <h2 className="text-2xl text-indigo-600">기본 정보 입력</h2>
              </div>
              <div className="space-y-10">
                {/* 수업 선택: staticLectureClass를 이용한 박스형 Select */}
                <div className="space-y-4">
                  <div>
                    <div className="space-x-2">
                      <span>수업 선택</span>
                      <span className="text-sm text-red-500">*</span>
                    </div>

                    <div className="text-sm text-gray-400">
                      필요하신 수업을 선택해주세요. 어떤 수업이든 젊고 능력있는
                      강사들이 수업을 진행합니다.
                    </div>
                  </div>
                  <RadioGroup
                    value={estimate.classType.toString()}
                    onValueChange={(value) =>
                      handleInputChange("classType", parseInt(value))
                    }
                    className="grid grid-cols-1 lg:grid-cols-2 lg:gap-x-4 lg:gap-y-2 gap-2"
                  >
                    {staticLectureClass
                      .filter(
                        (lecture) => lecture.type !== LectureType.university
                      )
                      .map((lecture, index) => (
                        <div
                          key={lecture.type}
                          className="flex items-center gap-3"
                        >
                          <RadioGroupItem
                            value={lecture.type.toString()}
                            id={`classTimes${lecture.type.toString()}`}
                          />
                          <Label
                            className={`border-2 rounded-lg px-3 py-2 flex items-center cursor-pointer w-full
                              ${
                                estimate.classType === lecture.type
                                  ? "bg-indigo-500 text-white border-indigo-500"
                                  : "bg-white hover:bg-gray-50"
                              }
                              `}
                            style={{
                              borderColor:
                                estimate.classType === lecture.type
                                  ? "#3b82f6"
                                  : "#eee",
                              borderWidth: "2px",
                              borderStyle: "solid",
                            }}
                            // onClick={() =>
                            //   handleInputChange("classType", lecture.type)
                            // }
                            htmlFor={`classTimes${lecture.type.toString()}`}
                          >
                            <img
                              src={lecture.thumbnail}
                              alt={lecture.name}
                              className="w-24 h-24 object-cover rounded-lg mr-4"
                            />
                            <div className="flex-1">
                              <div className="mb-2">{lecture.name}</div>
                              <div
                                className={`text-sm ${
                                  estimate.classType === lecture.type
                                    ? "text-blue-100"
                                    : "text-gray-500"
                                }`}
                              >
                                {lecture.description}
                              </div>
                            </div>
                          </Label>
                        </div>
                      ))}
                  </RadioGroup>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="space-x-2">
                      <span>수업 시간</span>
                      <span className="text-sm text-red-500">*</span>
                    </div>

                    <div className="text-sm text-gray-400">
                      수업 가능 하신 시간을 정해주세요. 수업 교시에 맞춰서
                      학습내용이 조정됩니다
                    </div>
                  </div>
                  {/* <span className="text-red-500">*</span> */}
                  <RadioGroup
                    value={estimate.classTimes.toString()}
                    onValueChange={(value) =>
                      handleInputChange("classTimes", parseInt(value))
                    }
                    className="flex items-center gap-8"
                  >
                    {classTimes.map((time) => (
                      <div key={time} className="flex items-center gap-2">
                        <RadioGroupItem
                          value={time.toString()}
                          id={`classTimes${time.toString()}}`}
                        />
                        <Label
                          htmlFor={`classTimes${time.toString()}}`}
                          className="text-gray-500 px-1 py-2"
                        >
                          {time} 차시
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="space-x-2">
                      <span>학교명</span>
                      <span className="text-sm text-red-500">*</span>
                    </div>

                    <div className="text-sm text-gray-400">
                      수업을 진행할 학교명을 입력해주세요
                    </div>
                  </div>
                  <div>
                    <Input
                      value={estimate.schoolName}
                      onChange={(e) =>
                        handleInputChange("schoolName", e.target.value)
                      }
                      placeholder="학교명을 입력하세요"
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="space-x-2">
                      <span>학급수</span>
                      <span className="text-sm text-red-500">*</span>
                    </div>
                    <div className="text-sm text-gray-400">
                      수업을 진행할 학급 수를 선택해주세요
                    </div>
                  </div>
                  <div className="w-40">
                    <Select
                      value={estimate.contractCnt.toString()}
                      onValueChange={(value) =>
                        handleInputChange("contractCnt", parseInt(value))
                      }
                    >
                      <SelectTrigger className="">
                        <SelectValue placeholder="학급수를 선택해주세요" />
                      </SelectTrigger>
                      <SelectContent className="z-[100] w-40">
                        {Array.from({ length: 20 }, (_, i) => i + 1).map(
                          (cnt) => (
                            <SelectItem
                              key={cnt}
                              value={cnt.toString()}
                              className="bg-white"
                            >
                              {cnt} 학급
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div>
                      <div className="space-x-2">
                        <span>수업 날짜 선택</span>
                        <span className="text-sm text-red-500">*</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        수업을 진행할 날짜를 선택해주세요
                      </div>
                    </div>
                    <div className="border rounded-lg bg-white w-full">
                      <div className="flex items-center justify-between p-4 border-b">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const prevMonth = new Date(
                              currentMonth.getFullYear(),
                              currentMonth.getMonth() - 1,
                              1
                            );
                            setCurrentMonth(prevMonth);
                          }}
                          className="flex items-center gap-1"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="15,18 9,12 15,6"></polyline>
                          </svg>
                          이전 달
                        </Button>
                        <div className="font-semibold">
                          {format(currentMonth, "yyyy년 M월", { locale: ko })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const nextMonth = new Date(
                              currentMonth.getFullYear(),
                              currentMonth.getMonth() + 1,
                              1
                            );
                            setCurrentMonth(nextMonth);
                          }}
                          className="flex items-center gap-1"
                        >
                          다음 달
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="9,18 15,12 9,6"></polyline>
                          </svg>
                        </Button>
                      </div>
                      <Calendar
                        mode="single"
                        selected={estimate.classDate}
                        month={currentMonth}
                        onMonthChange={setCurrentMonth}
                        disabled={(date) => {
                          const selectedDate = format(date, "yyyy-MM-dd");
                          const today = format(new Date(), "yyyy-MM-dd");
                          return selectedDate <= today;
                        }}
                        onSelect={(date) => {
                          if (date) {
                            handleInputChange("classDate", date);
                          }
                        }}
                        locale={ko}
                        className="w-full"
                        components={{
                          Caption: () => null,
                        }}
                        classNames={{
                          months:
                            "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 w-full",
                          month: "space-y-4 w-full",
                          caption:
                            "flex justify-center pt-1 relative items-center",
                          caption_label: "text-sm font-medium",
                          nav: "space-x-1 flex items-center",
                          nav_button:
                            "h-8 w-8 bg-gray-100 hover:bg-gray-200 p-0 rounded-full flex items-center justify-center transition-colors",
                          nav_button_previous: "absolute left-2",
                          nav_button_next: "absolute right-2",
                          table: "w-full border-collapse space-y-1",
                          head_row: "flex w-full",
                          head_cell:
                            "text-muted-foreground rounded-md w-full font-normal text-[0.8rem] p-2",
                          row: "flex w-full mt-2",
                          cell: "h-12 w-full text-center text-sm p-1 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-[60] z-[60]",
                          day: "h-10 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-gray-50 hover:text-blue-900 cursor-pointer transition-all duration-200 rounded-lg flex items-center justify-center text-gray-900",
                          day_selected:
                            "bg-indigo-600 text-white hover:bg-indigo-700 hover:text-white rounded-lg font-semibold shadow-md transform scale-105 transition-all duration-200 !text-white",
                          day_today:
                            "bg-gray-300 text-gray-900 rounded-lg font-medium",
                          day_outside: "text-muted-foreground opacity-50",
                          day_disabled: "text-gray-400 opacity-50",
                          day_range_middle:
                            "bg-blue-200 text-blue-900 rounded-lg",
                          day_range_start: "bg-indigo-500 text-white rounded-lg",
                          day_range_end: "bg-indigo-500 text-white rounded-lg",
                          day_hidden: "invisible",
                        }}
                        showOutsideDays={true}
                        fixedWeeks={true}
                      />
                    </div>
                    <div className="flex justify-center gap-4 mt-2 pb-12 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-indigo-500 rounded-lg"></div>
                        <span>선택된 날짜</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-gray-300 rounded-lg"></div>
                        <span>오늘</span>
                      </div>
                    </div>

                    {/* 선택된 결과 표시 */}
                    <ResultPage
                      estimate={estimate}
                      isDateSelected={isDateSelected}
                      classTimes={classTimes}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleNext}
                    className="bg-indigo-600 hover:bg-indigo-500"
                    disabled={!isEnableNext}
                  >
                    다음 단계
                  </Button>
                </div>
              </div>
            </>
          )}

          {step === 2 && !isCompleted && (
            <div className="space-y-10">
              <ResultPage
                estimate={estimate}
                isDateSelected={isDateSelected}
                classTimes={classTimes}
              />
              <div className="space-y-2">
                <div>
                  <div className="space-x-2">
                    <span>신청자 이름</span>
                    <span className="text-sm text-red-500">*</span>
                  </div>
                </div>
                <div>
                  <Input
                    ref={teacherNameInputRef}
                    value={estimate.teacherName}
                    onChange={(e) =>
                      handleInputChange("teacherName", e.target.value)
                    }
                    placeholder="이름을 입력하세요"
                    className="w-40"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <div className="space-x-2">
                    <span>신청자 이메일</span>
                    <span className="text-sm text-red-500">*</span>
                  </div>
                </div>
                <div>
                  <Input
                    value={estimate.teacherEmail}
                    onChange={(e) =>
                      handleInputChange("teacherEmail", e.target.value)
                    }
                    placeholder="이메일을 입력하세요"
                    className="w-60"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <div className="space-x-2">
                    <span>신청자 휴대폰</span>
                    <span className="text-sm text-red-500">*</span>
                  </div>
                </div>
                <div>
                  <Input
                    value={estimate.teacherPhone}
                    onChange={(e) =>
                      handleInputChange("teacherPhone", e.target.value)
                    }
                    placeholder="휴대폰 번호를 입력하세요"
                    className="w-60"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="pb-1">
                  <div className="font-semibold">수업문의 경로</div>
                  <div className="text-sm text-gray-400">
                    어떤 경로로 플러스멘토를 알게 되셨나요?
                  </div>
                </div>
                <RadioGroup
                  value={estimate.funnels}
                  onValueChange={(value) => handleInputChange("funnels", value)}
                  className="flex flex-wrap gap-6"
                >
                  {FunnelNames.map((funnel, index) => (
                    <div key={index} className="flex items-center">
                      <RadioGroupItem
                        value={(index + 1).toString()}
                        id={`funnel-${index + 1}`}
                      />
                      <Label
                        htmlFor={`funnel-${index + 1}`}
                        className="ml-2 text-gray-600 cursor-pointer"
                      >
                        {funnel}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* 서비스 동의 섹션 */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="font-semibold">서비스 동의</div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="agreeAll"
                      checked={
                        estimate.agreeTerms &&
                        estimate.agreePrivacy &&
                        estimate.agreeAd
                      }
                      onCheckedChange={(checked) => {
                        // 전체 동의 시 모든 항목을 동일한 상태로 설정
                        const newEstimate = {
                          ...estimate,
                          agreeTerms: checked === true,
                          agreePrivacy: checked === true,
                          agreeAd: checked === true,
                        };
                        setEstimate(newEstimate);
                        setIsEnableSubmit(checkValid2(newEstimate));
                      }}
                    />
                    <Label
                      htmlFor="agreeAll"
                      className="text-sm cursor-pointer"
                    >
                      전체 동의
                    </Label>
                  </div>
                </div>

                <div className="space-y-3 ml-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="agreeTerms"
                        checked={estimate.agreeTerms}
                        onCheckedChange={(checked) =>
                          handleInputChange("agreeTerms", checked)
                        }
                      />
                      <Label
                        htmlFor="agreeTerms"
                        className="text-sm cursor-pointer"
                      >
                        (필수)이용약관에 동의합니다.
                      </Label>
                    </div>
                    <button className="text-sm text-indigo-600 hover:text-blue-800">
                      자세히보기
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="agreePrivacy"
                        checked={estimate.agreePrivacy}
                        onCheckedChange={(checked) =>
                          handleInputChange("agreePrivacy", checked)
                        }
                      />
                      <Label
                        htmlFor="agreePrivacy"
                        className="text-sm cursor-pointer"
                      >
                        (필수)개인정보 수집 이용에 동의합니다.
                      </Label>
                    </div>
                    <button className="text-sm text-indigo-600 hover:text-blue-800">
                      자세히보기
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="agreeAd"
                        checked={estimate.agreeAd}
                        onCheckedChange={(checked) =>
                          handleInputChange("agreeAd", checked)
                        }
                      />
                      <Label
                        htmlFor="agreeAd"
                        className="text-sm cursor-pointer"
                      >
                        (선택)E-mail 및 SMS 광고성 정보 수신에 동의합니다.
                      </Label>
                    </div>
                    <button className="text-sm text-indigo-600 hover:text-blue-800">
                      자세히보기
                    </button>
                  </div>
                </div>

                <div className="ml-6 space-y-1">
                  <div className="text-xs text-gray-500">
                    * 지원사업 정보 및 무료 교사연수 정보를 제공해드립니다.
                  </div>
                  <div className="text-xs text-gray-500">
                    * 수업의 결과가 포함되어있는 사이트의 학교 전용 URL을
                    받아보실 수 있습니다
                  </div>
                </div>
              </div>
              <div className="flex justify-between">
                <Button onClick={handlePrev} variant="outline">
                  이전 단계
                </Button>

                <Button
                  onClick={handleSubmit}
                  className="bg-indigo-600 hover:bg-indigo-500"
                  disabled={!isEnableSubmit}
                >
                  {estimate.id === 0 ? "견적 문의하기" : "견적 수정하기"}
                </Button>
              </div>
            </div>
          )}

          {isCompleted && (
            <div className="space-y-4">
              <div className="text-center space-y-4">
                <div className="text-6xl mb-4">✅</div>
                <h2 className="text-3xl font-bold text-green-600">
                  {isEditMode
                    ? "견적 요청이 수정되었습니다!"
                    : "견적 요청이 완료되었습니다!"}
                </h2>
                <p className="text-lg text-gray-600">
                  빠른 시일 내에 입력해주신 연락처로 연락드리겠습니다.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  견적 요청 내용
                </h3>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3 text-lg">
                      수업 정보
                    </h4>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700 w-24">
                          견적ID
                        </span>
                        <span className="text-gray-900 ml-4">
                          {estimate.id}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700 w-24">
                          수업 종류
                        </span>
                        <span className="text-gray-900 ml-4">
                          {
                            staticLectureClass.find(
                              (lecture) => lecture.type === estimate.classType
                            )?.name
                          }
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700 w-24">
                          수업 시간
                        </span>
                        <span className="text-gray-900 ml-4">
                          {estimate.classTimes}차시
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700 w-24">
                          학교명
                        </span>
                        <span className="text-gray-900 ml-4">
                          {estimate.schoolName}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700 w-24">
                          학급수
                        </span>
                        <span className="text-gray-900 ml-4">
                          {estimate.contractCnt}학급
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700 w-24">
                          수업 날짜
                        </span>
                        <span className="text-gray-900 ml-4">
                          {format(estimate.classDate, "yyyy년 MM월 dd일", {
                            locale: ko,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 text-gray-500 font-medium rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3 text-lg">
                      신청자 정보
                    </h4>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-center">
                        <span className="font-medium text-blue-700 w-24">
                          신청자
                        </span>
                        <span className="text-blue-900 ml-4 font-semibold">
                          {estimate.teacherName}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-blue-700 w-24">
                          이메일
                        </span>
                        <span className="text-blue-900 ml-4">
                          {estimate.teacherEmail}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-blue-700 w-24">
                          연락처
                        </span>
                        <span className="text-blue-900 ml-4">
                          {estimate.teacherPhone}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center space-y-4">
                <div>
                  <p className="text-gray-600">
                    문의사항이 있으시면 언제든 연락주세요.
                  </p>
                  <p className="text-gray-600">(031)702-3336</p>
                </div>
                <div className="flex justify-center gap-4">
                  <Button
                    onClick={() => {
                      setIsCompleted(false);
                    }}
                    variant="outline"
                  >
                    이전 단계
                  </Button>
                  <Button
                    onClick={() => createNewEstimate()}
                    className="bg-indigo-600 hover:bg-indigo-500"
                  >
                    새로운 견적 요청하기
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EstimatePage;
